/**
 * WorkflowPane.jsx
 *
 * Main Workflow Checker panel — right SidePanel page.
 * Displays Gibbs-proposed workflows as editable step cards.
 * State machine: EMPTY → PROPOSED → EDITING → VALIDATED → SUBMITTED → COMPLETE
 */

import {
    useState, useCallback, useRef, useEffect, useMemo, useSyncExternalStore, memo,
} from 'react';
import debounce from 'lodash/debounce';
import {
    Box, Typography, Button, Collapse, IconButton,
    List, ListItem, ListItemText, ListItemSecondaryAction,
    TextField as MuiTextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { App } from 'BMapsSrc/BMapsApp';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { WebServices } from 'BMapsSrc/WebServices';
import { asIndividualTarget, getTargetInfoMap } from 'BMapsRedux/projectState/access';
import { getAction } from '../../../gibbs/registry';
import { dockingSiteChoice } from './dockingSiteChoice';
import { generateCampaignTitle } from './campaignTitle';
import { stepsExpansionSignal, nextStepsExpanded } from './stepsGroupState';
import { SidePanelContainer } from '../../info_display/SidePanel';
import { ErrorBoundary } from '../../PageWrapper';
import useEventBrokerSubscription from '../../common/helpers/useEventBrokerSubscription';
import useSelectedAtoms from '../../common/helpers/useSelectedAtoms';
import { WorkflowState, WorkflowEvents, WORKFLOW_PANEL_TITLE } from './constants';
import WorkflowSummary from './WorkflowSummary';
import WorkflowStepList from './WorkflowStepList';
import WorkflowCampaignPanel from './WorkflowCampaignPanel';
import { SessionNarrativeList } from '../../CampaignProgress';
import { useWorkflowValidation } from './useWorkflowValidation';
import CmdInterpreter from '../../../cmds/CmdInterpreter';
import {
    createFromActionPlan, validate, executeWorkflow,
    cancelWorkflow, ExecutionStatus,
    listWorkflows, loadWorkflow, deleteWorkflow,
    saveWorkflow as saveToLibrary,
    startRecording, stopRecording,
    saveMacro, getVariables, deriveTargetProtein,
} from '../../../gibbs/workflow';
import { findUnknownActions } from '../../../gibbs/text-to-action/validatePlan';
import { serializeWorkspace } from '../../../gibbs/text-to-action/serializeWorkspace';
import { applyModifications } from './applyModifications';
import { downloadData } from 'BMapsUI/ui_utils';
import {
    campaignBudgetReviewState,
    planWithStepParamOverrides,
} from './stepParamOverrides';
import {
    getPreference, savePreference,
} from '../../../gibbs/preferences';
import { shouldRefreshDeputyAuthCookie } from '../../../deputy/authRefresh';
import {
    reversedSelectedGrowVectorEvidence,
    selectedGrowVectorEvidence,
} from '../../../deputy/growVectorHelpers';
import { defaultCompoundAliasRegistry } from '../../../deputy/compoundAliasRegistry';
import { defaultCampaignArtifactRegistry } from '../../../deputy/campaignArtifactRegistry';
import {
    CampaignArtifactKind,
} from '../../../gibbs/autonomousCampaign/narrative/campaignArtifacts';
import {
    INITIAL_CAMPAIGN_PANEL_STATE,
    campaignEscalationReply,
    campaignReferenceRoleSummary,
    campaignStepFailureReason,
    completedCampaignStepIndex,
    reconnectRevokesCampaignSubmission,
    reduceCampaignPanelState,
    zapClearsWorkflowPane,
} from './campaignPanelState';
import {
    buildWorkflowSubmission,
    CAMPAIGN_AUTONOMY_MODES,
    DEFAULT_CAMPAIGN_AUTONOMY_MODE,
    campaignGrowCheckpointState,
    campaignDigestAfterSubmitFailure,
    isCampaignPlan,
    prepareWorkflowSubmission,
    workspaceSnapshotMatchesBaseline,
} from '../../../gibbs/autonomousCampaign/workflowSubmission';
import { crystalLigandRoleSummary } from '../../../gibbs/text-to-action/serializeWorkspace';
import { renderCampaignRefusalRecord } from '../../../gibbs/autonomousCampaign/narrative/refusalRecord';
import {
    SESSION_RESTORE_EVENT,
    sessionRestoreUnavailableReason,
} from '../../../server/sessionRestore';
import {
    CAMPAIGN_ALIAS_REFRESH_EVENTS,
    CAMPAIGN_DERIVED_DATA_EVENTS,
} from './campaignReviewEvents';

// Held between the reconnect zapping the workspace and the restore reporting
// back. Not a failure yet — just not a workspace anything may be measured on.
const RECONNECT_IN_PROGRESS_REASON = 'This workspace is being rebuilt after the session'
    + ' reconnected. Nothing can be measured on it until it comes back.';

const SUBMISSION_ABANDONED_MESSAGE = 'The session reconnected while this campaign was being'
    + ' signed, so it was not submitted. Review the workflow and submit again.';

/**
 * Why a submission was refused because the workspace moved under the review, and
 * whether the reviewer can recover by simply submitting again.
 *
 * `recoverable` is the difference between "the review is stale" and "the plan is
 * wrong". If the plan still validates against the workspace as it now stands, the
 * refreshed review is all that was needed and the next Start succeeds — nothing was
 * decided and nothing was refused, so this must not leave a refusal document
 * behind. If the plan no longer validates, the reviewer has to regenerate it, and
 * that is a refusal worth an exportable record.
 *
 * @returns {{message: string, recoverable: boolean}}
 */
function campaignWorkspaceReviewMessage(plan, workspace) {
    try {
        prepareWorkflowSubmission({ plan, workspace });
        return {
            recoverable: true,
            message: [
                'Workspace evidence changed while this workflow was under review.',
                'The review has been refreshed; confirm the steps and budget, then submit again.',
            ].join(' '),
        };
    } catch (_) {
        return {
            recoverable: false,
            message: [
                'Workspace state changed while this workflow was under review.',
                'Regenerate the workflow before submitting.',
            ].join(' '),
        };
    }
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.setOpen
 * @param {Function} props.handleClose
 * @param {boolean} [props.isStacked]
 */
// 2026-06-01: same React.memo pattern as HelpPanel — skip re-render
// during sidepanel resize drags. WorkflowPane carries a lot of state
// + child components; re-rendering it per drag mousemove contributed
// to the visible drag lag. Its props are stable during drag so memo
// bails out cleanly.
export default memo(WorkflowPaneImpl);

function WorkflowPaneImpl({
    open, setOpen, handleClose, isStacked,
}) {
    const [state, setState] = useState(WorkflowState.EMPTY);
    const [plan, setPlan] = useState(null); // ActionPlan from text-to-action
    const [stepParams, setStepParams] = useState({}); // { [stepIndex]: { ...overrides } }
    const [stepValidation, setStepValidation] = useState({}); // { [stepIndex]: StepStatus }
    const [results, setResults] = useState([]); // Per-step execution results
    // { [stepIndex]: { status, error } }
    const [executionStates, setExecutionStates] = useState({});
    const [provenanceSummary, setProvenanceSummary] = useState(null);
    const [provenanceLog, setProvenanceLog] = useState(null);
    // The short name the panel leads with (#145). Reviewed configuration like the
    // autonomy mode: generated when a plan is proposed, renameable, and cleared
    // with the plan it names. Empty means "derive it from the plan" — and it never
    // leaves this layer, because a user-typed name is user text on a masked
    // surface (#38).
    const [campaignTitle, setCampaignTitle] = useState('');
    // Read from the submit callback, which is created once per submit and can
    // outlive a rename: the auto-save runs after the run finishes, so the
    // closure's copy would be whatever the name was at submit time.
    const campaignTitleRef = useRef(campaignTitle);
    campaignTitleRef.current = campaignTitle;
    // The steps sit in one collapsible group, closed by default, so the panel
    // leads with the title, the execution method and the autonomy choice (#146).
    // It opens itself for anything that must not be hidden behind it — see
    // stepsGroupState.
    const [stepsExpanded, setStepsExpanded] = useState(false);
    const previousStepsSignalRef = useRef(null);
    // 2026-06-04 (W4): Surfacing preview-time precondition warnings.
    // Each entry: { nodeId, actionId, actionName, check, description }.
    // Populated by handleProposed via createFromActionPlan + validate.
    // Demo day 2026-06-03 surfaced the gap: "do a hit to lead on hep b"
    // emits a 10-step plan where step 5 (grow-from-atom) requires
    // `fragment_map_available` but no upstream step in the template
    // guarantees it. Without this surface, the workflow silently fails
    // at runtime when no fragment library is loaded.
    const [planWarnings, setPlanWarnings] = useState([]);
    const planRef = useRef(plan);
    planRef.current = plan;
    // Read by the zapAll handler, which is subscribed once and cannot see the
    // current `state` through its own closure.
    const stateRef = useRef(state);
    stateRef.current = state;
    const workflowRef = useRef(null);
    const deputyRef = useRef(null);
    // Assigned below, once the plan and the reviewed snapshot are in scope. The
    // campaign event handler is subscribed once and reaches it through here.
    const recordCampaignRefusalRef = useRef(null);
    // Ref for resolving failure decisions (retry/skip/abort)
    const failureResolverRef = useRef(null);
    // Tracks whether a workflow execution is currently in flight. Set true
    // just before the executor runs, cleared in finally. Read by handleZap
    // to guard against mid-execution wipes triggered by 'zapAll' event
    // broker events fired from action handlers (e.g., chooseProtein calls
    // App.Workspace.zap() to clear workspace state before loading a new
    // protein, which publishes 'zapAll' globally — without this guard,
    // WorkflowPane's handleZap would clear the plan + execution state
    // during step 1, hiding all subsequent step status from the user).
    //
    // See `Jess_Todos/Bmaps/exit83/worked-examples-for-milestone-checks-2026-06-08.md`
    // Example 4 / W1+W2 — this is the root cause of "workflow checker
    // collapses and empties mid-execution".
    const isExecutingRef = useRef(false);

    // ---- Saved Workflows state ----
    const [savedWorkflowsOpen, setSavedWorkflowsOpen] = useState(false);
    const [savedWorkflows, setSavedWorkflows] = useState([]);
    const [recording, setRecording] = useState(false);
    // Re-run dialog state: loaded workflow ready for parameter editing
    const [rerunWorkflow, setRerunWorkflow] = useState(null);
    const [rerunVariables, setRerunVariables] = useState([]);
    const [rerunValues, setRerunValues] = useState({});

    // Feature 3: Workflow auto-save prompt after completion
    const [showSavePrompt, setShowSavePrompt] = useState(false);
    const [completedWorkflow, setCompletedWorkflow] = useState(null);
    const [completedProvenance, setCompletedProvenance] = useState(null);
    const [saveMessage, setSaveMessage] = useState(null);
    // Precondition-warnings banner starts collapsed (top line only).
    const [warningsExpanded, setWarningsExpanded] = useState(false);

    // Feature 4: Better macro naming dialog
    const [showMacroNamer, setShowMacroNamer] = useState(false);
    const [macroName, setMacroName] = useState('');
    const [macroDescription, setMacroDescription] = useState('');

    // Stale-action guard: surfaced when a saved workflow references an
    // actionId that no longer exists in the registry. Blocks load so the
    // user doesn't see a ghost step that would fail at submit.
    const [loadError, setLoadError] = useState(null);
    const [campaignPanel, setCampaignPanel] = useState(INITIAL_CAMPAIGN_PANEL_STATE);
    // Session-scoped and outside this component on purpose: the artifact outlives
    // the run that produced it, the panel that showed it, and now the session — a
    // synchronous capture has to be able to read the list, and component state is
    // invisible to one.
    const { artifacts: campaignArtifacts } = useSyncExternalStore(
        defaultCampaignArtifactRegistry.subscribe,
        defaultCampaignArtifactRegistry.getSnapshot,
        defaultCampaignArtifactRegistry.getSnapshot,
    );
    // An earlier campaign the Orchestrator still holds unresolved. Reported
    // separately so it cannot masquerade as the state of a new proposal.
    const [unresolvedEarlierCampaign, setUnresolvedEarlierCampaign] = useState(null);
    // Set the moment a reconnect zaps the workspace, cleared only when the
    // restore reports success. While it holds a reason, everything on screen
    // describes a workspace that is not there, so no checkpoint may be answered
    // against it — the reply carries live atom selections and grow vectors read
    // straight out of App.Workspace.
    const [workspaceUnavailableReason, setWorkspaceUnavailableReason] = useState(null);
    const [liveNarrativeId, setLiveNarrativeId] = useState(null);
    const [campaignRejectedReason, setCampaignRejectedReason] = useState(null);
    // One masked Deputy snapshot backs both the visible budget review and signing.
    // Workspace events replace it; submission never silently reads a different one.
    const [campaignWorkspaceSnapshot, setCampaignWorkspaceSnapshot] = useState(null);
    const [campaignAutonomyMode, setCampaignAutonomyMode] = useState(
        DEFAULT_CAMPAIGN_AUTONOMY_MODE,
    );
    const campaignDigestRef = useRef(null);
    const planRevisionRef = useRef(0);
    const submitAttemptRef = useRef(0);
    useSyncExternalStore(
        defaultCompoundAliasRegistry.subscribe,
        defaultCompoundAliasRegistry.getSnapshot,
        defaultCompoundAliasRegistry.getSnapshot,
    );
    const formatCampaignCompoundId = useCallback((maskedId) => (
        defaultCompoundAliasRegistry.formatCompoundId(maskedId, { includeMaskedId: false })
    ), []);
    const formatCampaignText = useCallback((text) => (
        defaultCompoundAliasRegistry.formatText(text, { includeMaskedId: false })
    ), []);
    const formatCampaignMarkdown = useCallback((text) => (
        defaultCompoundAliasRegistry.formatMarkdown(text, { includeMaskedId: false })
    ), []);
    // A stored document decodes with the bindings it was written with, not with the
    // live workspace's. Same rendering, different source of truth — and the only
    // one that still holds after a restore, or for a narrative from an earlier
    // workspace this session also loaded.
    const artifactMarkdownFormatter = useCallback((artifact) => (
        defaultCampaignArtifactRegistry.markdownFormatterFor(artifact)
    ), []);
    const selectedAtoms = useSelectedAtoms();
    const [growVectorReversed, setGrowVectorReversed] = useState(false);
    const campaignPlan = isCampaignPlan(plan);
    const campaignHasGrow = campaignPlan
        && (plan?.actions || []).some((action) => action.actionId === 'grow-from-atom');
    // Disclosure only, and browser-side only: real display names never enter the
    // signed submission or the narrative.
    //
    // Read live on every render rather than memoized. Setting a compound's
    // native-reference role does not reliably emit a compound event, so a
    // snapshot-keyed memo would keep reporting a stale role after the user had
    // changed it. The scan is a heritage read over the loaded compounds, which
    // is cheap at this size.
    const referenceRoleSummary = campaignPlan
        ? campaignReferenceRoleSummary(crystalLigandRoleSummary(App.Workspace))
        : null;
    // A collapsed steps group must not swallow a checkpoint waiting on a human or
    // a step that failed. The signal's identity is what forces it open, so a
    // deliberate collapse survives while the same condition persists.
    const stepsSignal = stepsExpansionSignal({
        escalation: campaignPanel?.escalation,
        executionStates,
        stepValidation,
        results,
    });
    useEffect(() => {
        setStepsExpanded((expanded) => nextStepsExpanded({
            signal: stepsSignal,
            previousSignal: previousStepsSignalRef.current,
            expanded,
        }));
        previousStepsSignalRef.current = stepsSignal;
    }, [stepsSignal]);
    // Which structure this campaign docks into. Read live from the workspace and
    // the project target list rather than from the masked snapshot, because the
    // reviewer needs real names to choose between structures — the masking
    // boundary governs what leaves for the Orchestrator, not what the data's
    // owner sees in their own panel.
    const dockingSiteStructures = useMemo(() => (campaignPlan
        ? (App.Workspace?.allCaseData?.() || [])
            .filter((caseData) => caseData?.mapCase)
            .map(({ mapCase }) => ({
                key: mapCase.uri || mapCase.displayName || mapCase.name,
                name: mapCase.displayName || mapCase.name || 'unnamed structure',
                isTarget: asIndividualTarget(getTargetInfoMap().get(mapCase))?.isTarget === true,
                mapCase,
            }))
        : []
    // campaignWorkspaceSnapshot is a recompute TRIGGER, not an input: writing a
    // role re-reads the snapshot, and that is what has to pull the picker's
    // selection back in step with what the contract will read. The exhaustive-deps
    // rule cannot express that, so it reports the dependency as unnecessary.
    // Removing it leaves the picker showing the previous choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [campaignPlan, campaignWorkspaceSnapshot]);
    const dockingSite = dockingSiteChoice(dockingSiteStructures);
    // The site is part of the signed submission, so it stops being a control once
    // THIS plan is submitted — and becomes one again the moment a new plan is
    // proposed. Deriving that from the plan lifecycle rather than from the
    // campaign panel's event history matters: those events survive a reload of an
    // existing Deputy session, so a freshly proposed workflow would otherwise
    // inherit the previous run's lock and be impossible to configure.
    const dockingSiteLocked = state === WorkflowState.SUBMITTED
        || state === WorkflowState.COMPLETE;
    // Keeps the rejection reason instead of collapsing every failure into a
    // single "waiting" message: a rejected step edit is not a pending
    // calculation, and reporting it as one left the panel permanently stuck
    // with no hint of what to undo.
    const campaignBudgetReview = useMemo(() => (
        campaignPlan
            ? campaignBudgetReviewState({
                plan,
                stepParams,
                workspaceSnapshot: campaignWorkspaceSnapshot,
            })
            : { derivation: null, blockedReason: null }
    ), [campaignPlan, plan, stepParams, campaignWorkspaceSnapshot]);
    const campaignBudgetDerivation = campaignBudgetReview.derivation;

    // Refresh saved workflows list when section is opened
    useEffect(() => {
        if (savedWorkflowsOpen) {
            setSavedWorkflows(listWorkflows());
        }
    }, [savedWorkflowsOpen]);

    // What earlier sessions left behind. This is what makes a narrative survive a
    // page reload and a new tab, rather than only a panel reset.
    useEffect(() => {
        defaultCampaignArtifactRegistry.hydrate();
    }, []);

    // Feature 5: Workflow progress persistence
    const [showResumePrompt, setShowResumePrompt] = useState(false);
    const [savedProgress, setSavedProgress] = useState(null);

    const PROGRESS_KEY = 'gibbs.workflowProgress';

    const saveProgress = useCallback((summaryText, states, planToSave, paramsToSave) => {
        try {
            localStorage.setItem(PROGRESS_KEY, JSON.stringify({
                planSummary: summaryText,
                stepStates: states,
                // Persist the full plan + stepParams so Resume can
                // actually rehydrate the workflow. Without these the
                // resume prompt had nothing to restore and was dead UI.
                plan: planToSave || null,
                stepParams: paramsToSave || null,
                lastUpdate: new Date().toISOString(),
            }));
        } catch (_) { /* ignore quota errors */ }
    }, []);

    const handleResumeWorkflow = useCallback(() => {
        if (!savedProgress?.plan) {
            setShowResumePrompt(false);
            return;
        }
        // Same stale-action guard as loadWorkflowAsPlan. A plan persisted
        // before an action was removed would restore a broken step.
        const unknown = findUnknownActions(savedProgress.plan);
        if (unknown.length > 0) {
            setLoadError({
                name: savedProgress.planSummary || 'Previous workflow',
                unknownActions: [...new Set(unknown)],
            });
            setShowResumePrompt(false);
            return;
        }
        setPlan(savedProgress.plan);
        setStepParams(savedProgress.stepParams || {});
        setExecutionStates(savedProgress.stepStates || {});
        setStepValidation({});
        setResults([]);
        setState(WorkflowState.EDITING);
        setCampaignAutonomyMode(DEFAULT_CAMPAIGN_AUTONOMY_MODE);
        setCampaignTitle('');
        setShowResumePrompt(false);
        setOpen(true);
    }, [savedProgress, setOpen]);

    const clearProgress = useCallback(() => {
        try {
            localStorage.removeItem(PROGRESS_KEY);
        } catch (_) { /* ignore */ }
    }, []);

    // Check for saved progress on mount
    useEffect(() => {
        try {
            const raw = localStorage.getItem(PROGRESS_KEY);
            if (raw) {
                const progress = JSON.parse(raw);
                if (progress?.planSummary) {
                    setSavedProgress(progress);
                    setShowResumePrompt(true);
                }
            }
        } catch (_) { /* ignore parse errors */ }
    }, []);

    // Feature 4: macro naming save handler
    const pendingMacroRef = useRef(null);

    const handleSaveMacro = useCallback(() => {
        if (!pendingMacroRef.current) return;
        const name = macroName.trim() || 'Untitled Macro';
        saveMacro(
            pendingMacroRef.current,
            name,
            macroDescription.trim() || undefined
        );
        pendingMacroRef.current = null;
        setShowMacroNamer(false);
        setMacroName('');
        setMacroDescription('');
        if (savedWorkflowsOpen) {
            setSavedWorkflows(listWorkflows());
        }
    }, [macroName, macroDescription, savedWorkflowsOpen]);

    const handleCancelMacroNamer = useCallback(() => {
        pendingMacroRef.current = null;
        setShowMacroNamer(false);
        setMacroName('');
        setMacroDescription('');
    }, []);

    // ---- Macro recording handlers ----
    const handleToggleRecording = useCallback(() => {
        if (recording) {
            try {
                const { workflow } = stopRecording();
                // Protein-picker (Path A): if the macro captured a deliberate
                // protein load, auto-parameterize its pdbId as `targetProtein`
                // so the existing re-run dialog lets the user re-target the
                // macro to a different protein on replay. No-op (returns null)
                // when no load was captured — that macro stays protein-agnostic
                // (Path B harness-load re-ask is a follow-up; see the plan).
                deriveTargetProtein(workflow);
                pendingMacroRef.current = workflow;
                setShowMacroNamer(true);
            } catch (err) {
                // stopRecording throws "No actions were captured" when nothing
                // was recorded. Previously this was swallowed silently, so the
                // user hit Stop and saw nothing. Surface it instead.
                console.warn(
                    '[WorkflowPane] Stop recording failed:',
                    err.message
                );
                setSaveMessage(
                    'No actions were captured — nothing to save as a macro. '
                    + 'Recording captures actions you run while it is active.'
                );
            }
            setRecording(false);
        } else {
            startRecording();
            setRecording(true);
        }
    }, [recording]);

    // ---- Load saved workflow handler ----
    const handleLoadWorkflow = useCallback((id) => {
        const loaded = loadWorkflow(id);
        if (!loaded) return;
        const { workflow, metadata } = loaded;

        // Extract variables for the re-run dialog
        const vars = getVariables(workflow);
        if (vars.length > 0) {
            setRerunWorkflow({ id, workflow, metadata });
            setRerunVariables(vars);
            const defaults = {};
            vars.forEach((v) => { defaults[v.variableName] = v.defaultValue; });
            setRerunValues(defaults);
        } else {
            // No variables — load directly as a proposed plan
            loadWorkflowAsPlan(workflow, metadata);
        }
    }, []);

    const loadWorkflowAsPlan = useCallback((workflow, metadata) => {
        // Convert workflow nodes to an ActionPlan-like structure for the step list
        const nodes = workflow.getNodes();
        const actions = nodes.map((node) => ({
            actionId: node.actionId,
            params: { ...node.paramOverrides },
        }));
        const fakePlan = {
            intent: nodes.length > 1 ? 'workflow' : 'single_action',
            actions,
            summary: metadata?.name || 'Loaded Workflow',
        };
        // Stale-action guard. A workflow saved at time T may reference an
        // action that was later removed or renamed. Rendering a ghost step
        // would break at submit; block the load and tell the user which
        // action is missing so they can delete and recreate.
        const unknown = findUnknownActions(fakePlan);
        if (unknown.length > 0) {
            setLoadError({
                name: metadata?.name || 'Workflow',
                unknownActions: [...new Set(unknown)],
            });
            setRerunWorkflow(null);
            setRerunVariables([]);
            setRerunValues({});
            return;
        }
        setLoadError(null);
        setPlan(fakePlan);
        const params = {};
        actions.forEach((action, i) => { params[i] = { ...action.params }; });
        setStepParams(params);
        setStepValidation({});
        setResults([]);
        setState(WorkflowState.PROPOSED);
        setCampaignAutonomyMode(DEFAULT_CAMPAIGN_AUTONOMY_MODE);
        // A saved workflow arrives with its own name as the summary, so the title
        // derives from the plan rather than from a request nobody typed.
        setCampaignTitle('');
        setRerunWorkflow(null);
        setRerunVariables([]);
        setRerunValues({});
    }, []);

    const handleRerunVariableChange = useCallback((variableName, value) => {
        setRerunValues((prev) => ({ ...prev, [variableName]: value }));
    }, []);

    const handleRerunExecute = useCallback(() => {
        if (!rerunWorkflow) return;
        // Apply variable values to the workflow and load it
        const { workflow, metadata } = rerunWorkflow;
        const nodes = workflow.getNodes();
        for (const node of nodes) {
            for (const [paramName, value] of Object.entries(node.paramOverrides)) {
                if (value && typeof value === 'object' && value.type === 'variable') {
                    const resolved = rerunValues[value.name] !== undefined
                        ? rerunValues[value.name]
                        : value.defaultValue;
                    node.paramOverrides[paramName] = resolved;
                }
            }
        }
        loadWorkflowAsPlan(workflow, metadata);
    }, [rerunWorkflow, rerunValues, loadWorkflowAsPlan]);

    // ---- Delete saved workflow handler ----
    const handleDeleteWorkflow = useCallback((id) => {
        deleteWorkflow(id);
        setSavedWorkflows(listWorkflows());
    }, []);

    // ---- Event: Gibbs proposed a new workflow ----
    const handleProposed = useCallback((_evt, data) => {
        const { plan: newPlan, request } = data;
        setPlan(newPlan);
        // Named from what the user asked, at five words maximum. `plan.summary`
        // is signed and stays exactly as the builder wrote it.
        setCampaignTitle(generateCampaignTitle(newPlan, request));
        // Initialize stepParams from plan actions
        const params = {};
        (newPlan.actions || []).forEach((action, i) => {
            params[i] = { ...action.params };
        });
        setStepParams(params);
        setStepValidation({});
        setResults([]);
        // Reset per-step execution status so a prior workflow's green
        // "Completed" badges don't bleed into a newly proposed workflow
        // just because the step indexes collide.
        setExecutionStates({});
        setProvenanceSummary(null);
        setProvenanceLog(null);
        setCampaignAutonomyMode(DEFAULT_CAMPAIGN_AUTONOMY_MODE);
        // 2026-06-04 (W4): compute preview-time precondition warnings
        // for the proposed plan. Builds the DAG and runs validate() to
        // surface unsatisfiedPreconditions so the user sees them BEFORE
        // submit instead of getting a silent runtime failure. Wrapped
        // in try/catch so a malformed plan never blocks the proposal
        // display — worst case the warnings are empty.
        //
        // 2026-06-09 (Fix B1): filter out warnings whose precondition
        // is ALREADY SATISFIED by the current workspace state. The DAG
        // validator only walks edge-connected upstream postconditions
        // — it has no view of ambient workspace state. So when the
        // user has 4RMG + 3TE.402:A loaded, the validator still flags
        // protein_loaded / compound_loaded on every downstream step
        // whose upstream edges don't carry those guarantees (which
        // happens whenever a template edge was removed for type safety
        // — see PR #60). Cross-referencing `validation.
        // unsatisfiedPreconditions` against `workspaceState.
        // preconditionsMet` lets the banner show only the REAL gaps —
        // typically `fragment_map_available` and `internet_available`
        // when no fragment library is loaded and MapLight is
        // reachable.
        //
        // serializeWorkspace runs `evaluatePrecondition` for every
        // registered precondition ID and lists the ones the current
        // workspace satisfies. We use that list as the filter set.
        let warnings = [];
        try {
            const { workflow } = createFromActionPlan(newPlan);
            const validation = validate(workflow);
            const workspaceState = serializeWorkspace(App.Workspace);
            const ambientSatisfied = new Set(
                (workspaceState && workspaceState.preconditionsMet) || [],
            );
            if (validation
                && Array.isArray(validation.unsatisfiedPreconditions)) {
                warnings = validation.unsatisfiedPreconditions
                    .filter((u) => !ambientSatisfied.has(u.check))
                    .map((u) => {
                        const node = workflow.getNode(u.nodeId);
                        const actionDef = node ? getAction(node.actionId) : null;
                        return {
                            nodeId: u.nodeId,
                            actionId: node ? node.actionId : null,
                            actionName: actionDef ? actionDef.name : (node ? node.actionId : 'unknown'),
                            check: u.check,
                            description: u.description,
                        };
                    });
            }
        } catch (err) {
            // Plan couldn't be DAG-built — fall back to no warnings.
            warnings = [];
        }
        setPlanWarnings(warnings);
        setState(WorkflowState.PROPOSED);
        setOpen(true);
    }, [setOpen]);

    useEventBrokerSubscription(WorkflowEvents.PROPOSED, handleProposed);

    // ---- Event: Conversational modification applied ----
    const handleModified = useCallback((_evt, data) => {
        if (data.modifications && planRef.current) {
            // Apply plan diff from modify_workflow intent
            const { plan: updatedPlan, params: updatedParams } = applyModifications(
                planRef.current, stepParams, data.modifications
            );
            setPlan(updatedPlan);
            setStepParams(updatedParams);
        } else {
            // Direct plan/params replacement
            if (data.plan) {
                setPlan(data.plan);
            }
            if (data.params) setStepParams(data.params);
        }
        setState(WorkflowState.EDITING);
    }, [stepParams]);

    useEventBrokerSubscription(WorkflowEvents.MODIFIED, handleModified);

    // ---- Event: zapAll resets everything ----
    const handleZap = useCallback((_evt, zapEventData) => {
        // Guard (W2): skip the wipe while a workflow execution is in flight —
        // step 1's load-protein-from-pdb handler calls App.Workspace.zap()
        // ('zapAll' globally); wiping the plan + execution state mid-run makes
        // steps 2+ invisible (step 1 succeeds, then the panel empties). Explicit
        // user cancels go through handleCancelWorkflow, which clears the
        // execution lifecycle before the wipe propagates.
        if (isExecutingRef.current) return;
        // ...except when the zap is a reconnect on its way to restoring this
        // very session. A campaign lives in the Orchestrator, so it survives the
        // connection going away; the plan, the step states and the narrative on
        // screen do not, and `SavedState` has never carried any of them. Nothing
        // is cancelled either: ending a campaign is the user's decision, not a
        // side effect of clicking Reload Session.
        if (!zapClearsWorkflowPane(zapEventData, {
            holdsCampaign: isCampaignPlan(planRef.current),
        })) {
            // Spared, but not pretended intact. Until the restore reports back,
            // this workspace is gone, and the reason gates anything that would
            // dispatch against it.
            setWorkspaceUnavailableReason(RECONNECT_IN_PROGRESS_REASON);
            // A submission caught mid-signing must not go on to submit. Clearing
            // the plan used to invalidate it as a side effect: the post-signing
            // guard compares planRef against the plan it started with. Sparing
            // the plan removed that, so the attempt is now revoked outright —
            // otherwise a campaign gets signed against the workspace that just
            // went away and submitted into the one being rebuilt.
            if (reconnectRevokesCampaignSubmission({
                workflowState: stateRef.current,
                campaignDigest: campaignDigestRef.current,
            })) {
                submitAttemptRef.current += 1;
                setCampaignPanel((prev) => reduceCampaignPanelState(prev, {
                    type: 'campaign-status',
                    status: 'rejected',
                    message: SUBMISSION_ABANDONED_MESSAGE,
                }));
                setResults([{ success: false, error: SUBMISSION_ABANDONED_MESSAGE }]);
                // Back to a state the user can act on. The revoked attempt
                // returns silently, so leaving this SUBMITTED would strand the
                // pane on a submission that is never coming.
                setState(WorkflowState.EDITING);
            }
            return;
        }

        cancelWorkflow();
        if (failureResolverRef.current) {
            failureResolverRef.current('abort');
            failureResolverRef.current = null;
        }
        setPlan(null);
        setCampaignTitle('');
        setStepParams({});
        setStepValidation({});
        setResults([]);
        setExecutionStates({});
        setProvenanceSummary(null);
        setProvenanceLog(null);
        setCampaignAutonomyMode(DEFAULT_CAMPAIGN_AUTONOMY_MODE);
        setCampaignTitle('');
        setPlanWarnings([]);
        setState(WorkflowState.EMPTY);
    }, []);

    useEventBrokerSubscription('zapAll', handleZap);

    // ---- Event: the reconnect reports whether the workspace came back ----
    const handleSessionRestore = useCallback((_evt, restoreEventData) => {
        setWorkspaceUnavailableReason(sessionRestoreUnavailableReason(restoreEventData));
    }, []);

    useEventBrokerSubscription(SESSION_RESTORE_EVENT, handleSessionRestore);

    const handleCampaignEvent = useCallback((_evt, event = {}) => {
        // Does this page own the campaign the event is about? The digest is set at
        // submit and cleared at completion, so "no digest" means nothing was
        // launched from here.
        const ownsCampaign = campaignDigestRef.current !== null;
        // An escalation, or a resume record, for a campaign this page never
        // submitted belongs to an earlier run the Orchestrator still holds — an
        // interrupted session parked in `recovery-required` gets its runner
        // restarted on any reconnect. Feeding that to the live reducer set
        // `running: true` and populated `escalation`, which put a Retry Action
        // prompt and a disabled autonomy selector on top of a workflow the user
        // had only just proposed. Report it; do not let it drive this proposal.
        const earlierCampaignControl = !ownsCampaign && (
            event.type === 'escalation'
            || (event.type === 'decision-record' && event.record?.actionId === 'campaign-resume')
        );
        if (earlierCampaignControl) {
            setUnresolvedEarlierCampaign(event.type === 'escalation' ? event : null);
            return;
        }
        const replayedCompletion = event.type === 'campaign-complete' && !ownsCampaign;
        // A replayed completion is recorded as a previous narrative below. Letting
        // it into the live event stream ALSO rendered a completion block for it, so
        // one narrative appeared twice — once as if it had just happened.
        if (!replayedCompletion) {
            setCampaignPanel((prev) => reduceCampaignPanelState(prev, event));
        }
        if (event.type === 'decision-record') {
            setExecutionStates((prev) => {
                const actions = planRef.current?.actions || [];
                const index = completedCampaignStepIndex(actions, prev, event.record);
                return index < 0 ? prev : {
                    ...prev,
                    [index]: { status: ExecutionStatus.SUCCEEDED },
                };
            });
        }
        if (event.type === 'campaign-complete') {
            // Every narrative this session produced stays reachable, whichever
            // campaign made it — her 07-28 ask. The panel's own state is reset by
            // the next submit, by closing the pane, and by a zap, each of which
            // used to take the finished run's only download button with it.
            const historical = !ownsCampaign;
            const artifactId = Number.isInteger(event.frameSeq)
                ? `narrative-seq-${event.frameSeq}`
                : `narrative-${event.status || 'complete'}`;
            // The live completion block already shows this one. Remembering which
            // keeps it out of the previous-narratives box until the panel resets,
            // at which point it becomes exactly that: a previous narrative.
            if (!historical) setLiveNarrativeId(artifactId);
            defaultCampaignArtifactRegistry.record({
                id: artifactId,
                kind: CampaignArtifactKind.Narrative,
                status: event.status,
                narrative: event.narrative,
                // The bindings as they stand now, copied into the record. The live
                // registry's map is keyed by a workspace fingerprint that includes
                // compound order, so a document that leaned on it would stop
                // decoding the moment a restore reordered the compounds.
                aliasBindings: defaultCompoundAliasRegistry.getSnapshot(),
                label: historical
                    ? `Earlier campaign — ${event.status || 'complete'}`
                    : `Campaign ${event.status || 'complete'}`,
            });
            // A completion replayed by the Orchestrator on reconnect describes a
            // run that ended before this page existed. Letting it drive the live
            // panel reported a finished campaign as if it had just happened, and
            // left a freshly proposed workflow sitting under someone else's steps.
            if (historical) return;
            if (event.status !== 'complete') {
                setExecutionStates((prev) => {
                    const next = { ...prev };
                    const total = planRef.current?.actions?.length || 0;
                    for (let index = 0; index < total; index += 1) {
                        if (next[index]?.status === ExecutionStatus.SUCCEEDED) continue;
                        next[index] = event.status === 'cancelled'
                            ? { status: ExecutionStatus.CANCELLED }
                            : {
                                status: ExecutionStatus.FAILED,
                                error: campaignStepFailureReason(event.status),
                            };
                    }
                    return next;
                });
            }
            setState(WorkflowState.COMPLETE);
            campaignDigestRef.current = null;
        }
        if (event.type === 'campaign-status' && event.status === 'rejected') {
            // The other refusal that leaves no narrative: the Orchestrator nacked
            // the submission, so no campaign-complete frame will ever arrive.
            const reason = event.message || 'The campaign submission was rejected.';
            setCampaignRejectedReason(reason);
            setResults([{ success: false, error: event.message }]);
            recordCampaignRefusalRef.current?.({ reason, stage: 'submission' });
            setState(WorkflowState.EDITING);
            campaignDigestRef.current = null;
        }
    }, []);

    const refreshCampaignCompoundAliases = useCallback(() => {
        const deputy = deputyRef.current;
        deputy?.refreshCompoundAliases?.();
        const snapshot = deputy?.stateReader?.readForReadResult?.() || null;
        setCampaignWorkspaceSnapshot(snapshot);
    }, []);
    // Picking writes the SAME target flag the Project / Targets checkbox writes,
    // through the same action, so the two panels cannot drift into disagreeing
    // about which structure the campaign will dock into. Changing the role
    // rebuilds the protein tree but publishes only `atomGroupStateChanged`, which
    // the campaign snapshot does not listen for — so re-read it here, or the
    // panel would keep reviewing against the roles the reviewer just changed.
    const handleDockingSiteChange = useCallback(async (key) => {
        // Imported here rather than at module scope to leave this file's lazy
        // loading of the command index as it was.
        const { UserActions } = await import('../../../cmds');
        dockingSiteStructures.forEach((structure) => {
            const isTarget = structure.key === key;
            if (structure.isTarget === isTarget) return;
            UserActions.SetProteinTarget(structure.mapCase, {
                mapCase: structure.mapCase,
                displayName: structure.name,
                isTarget,
            });
        });
        refreshCampaignCompoundAliases();
    }, [dockingSiteStructures, refreshCampaignCompoundAliases]);

    const refreshCampaignCompoundAliasesDebounced = useMemo(() => (
        debounce(refreshCampaignCompoundAliases, 100)
    ), [refreshCampaignCompoundAliases]);

    useEventBrokerSubscription(
        CAMPAIGN_ALIAS_REFRESH_EVENTS,
        refreshCampaignCompoundAliases,
    );
    useEventBrokerSubscription(
        ['compoundChanged', ...CAMPAIGN_DERIVED_DATA_EVENTS],
        refreshCampaignCompoundAliasesDebounced,
    );

    useEffect(() => () => {
        refreshCampaignCompoundAliasesDebounced.cancel();
    }, [refreshCampaignCompoundAliasesDebounced]);

    useEffect(() => {
        let deputy;
        let cancelled = false;
        (async () => {
            const [{ Loader }, deputyMod, { UserActions }] = await Promise.all([
                import('../../../Loader'),
                import('../../../deputy'),
                import('../../../cmds'),
            ]);
            if (cancelled) return;
            if (!deputyMod.isDeputyEnabled({ enabled: Loader.AutonomousCampaignEnabled })) {
                setCampaignWorkspaceSnapshot(null);
                handleCampaignEvent(null, { type: 'campaign-status', status: 'disabled' });
                return;
            }

            deputy = await deputyMod.initDeputyAfterAuth({
                enabled: Loader.AutonomousCampaignEnabled,
                prepareAuth: shouldRefreshDeputyAuthCookie()
                    ? () => WebServices.refreshCookie()
                    : undefined,
                getWorkspace: () => App.Workspace,
                // The target/off-target role a reviewer sets in the Protein
                // panel. It lives in the Redux project target list keyed by
                // MapCase object, and neither the Deputy nor the gibbs
                // serializer may reach into the store, so it is injected here
                // next to the workspace itself. A structure that is only a
                // curated-panel member resolves to its group entry, which
                // asIndividualTarget deliberately rejects: panel membership is
                // not a docking-site decision.
                getProteinRole: (mapCase) => {
                    const entry = asIndividualTarget(getTargetInfoMap().get(mapCase));
                    if (!entry) return null;
                    return entry.isTarget ? 'target' : 'off-target';
                },
                getDataConnection: () => App.PrimaryDataConnection,
                userActions: UserActions,
                onCampaignEvent: (evt) => {
                    if (!cancelled) handleCampaignEvent(null, evt);
                },
                onEscalationEvent: (evt) => {
                    if (!cancelled) handleCampaignEvent(null, evt);
                },
            });
            if (cancelled) {
                deputy.stop();
                return;
            }
            if (deputy.authError) {
                // eslint-disable-next-line no-console
                console.warn('Autonomous Campaign Deputy auth refresh failed:', deputy.authError);
            }
            deputyRef.current = deputy;
            refreshCampaignCompoundAliases();
            handleCampaignEvent(null, {
                type: 'campaign-status',
                status: deputy.enabled ? 'ready' : 'disabled',
            });
        })();
        return () => {
            cancelled = true;
            if (deputy) deputy.stop();
            deputyRef.current = null;
        };
    }, [handleCampaignEvent, refreshCampaignCompoundAliases]);

    const selectedAtomUniqueIds = useCallback(() => {
        if (!Array.isArray(selectedAtoms)) return [];
        return selectedAtoms
            .map((atom) => (atom?.uniqueID == null ? null : String(atom.uniqueID)))
            .filter(Boolean);
    }, [selectedAtoms]);
    const selectedAtomKey = useMemo(() => selectedAtomUniqueIds().join('|'), [selectedAtomUniqueIds]);

    useEffect(() => {
        setGrowVectorReversed(false);
    }, [selectedAtomKey]);

    const selectedGrowVector = useMemo(() => {
        const evidence = selectedGrowVectorEvidence(selectedAtoms, App.Workspace);
        return growVectorReversed
            ? reversedSelectedGrowVectorEvidence(evidence)
            : evidence;
    }, [selectedAtoms, growVectorReversed]);
    const handleGrowVectorReverseChange = useCallback((reversed) => {
        setGrowVectorReversed(reversed);
    }, []);
    // Human-in-the-loop grow direction is collected only after execution reaches
    // the parent-specific grow step. Full-auto resolves bounded candidate vectors
    // in the live bridge. Neither mode asks for atoms before the campaign starts.
    const {
        requiredDuringExecution: campaignGrowCheckpointRequired,
    } = campaignGrowCheckpointState({
        hasGrow: campaignHasGrow,
        autonomyMode: campaignAutonomyMode,
    });
    const campaignSubmitBlockedReason = campaignBudgetReview.blockedReason;
    // Her 07-28 ask: a refusal has to be exportable too. Built from the same three
    // things the refusal itself was decided on — the reviewed plan, the masked
    // snapshot, and the reason the panel is showing — so the document cannot claim
    // anything the refusal did not know. The timestamp is stamped here rather than
    // inside the renderer, which stays pure.
    const campaignRefusalRecord = useMemo(() => {
        const reason = campaignPlan
            ? (campaignBudgetReview.blockedReason || campaignRejectedReason)
            : null;
        if (!reason) return '';
        return renderCampaignRefusalRecord({
            plan: planWithStepParamOverrides(plan, stepParams),
            workspace: campaignWorkspaceSnapshot,
            reason,
            autonomyMode: campaignAutonomyMode,
            at: new Date().toISOString(),
            stage: campaignBudgetReview.blockedReason ? 'review' : 'submission',
        });
    }, [
        campaignPlan, campaignBudgetReview.blockedReason, campaignRejectedReason,
        plan, stepParams, campaignWorkspaceSnapshot, campaignAutonomyMode,
    ]);
    // A refusal is a document too — error messages have to be exportable, and a
    // refused run produced nothing else. Kept at the two moments a refusal is
    // final: the submission contract refusing at submit, and the Orchestrator
    // nacking one. Deliberately NOT on the review pane's live blocked reason,
    // which appears and clears while a reviewer edits parameters and would fill
    // the list with documents for states nobody decided anything in.
    const recordCampaignRefusal = useCallback(({ plan: refusedPlan, reason, stage }) => {
        const planForRecord = refusedPlan
            || planWithStepParamOverrides(planRef.current, stepParams);
        if (!reason || !isCampaignPlan(planForRecord)) return;
        defaultCampaignArtifactRegistry.record({
            // Same reason at the same stage is the same refusal, not a second one.
            id: `refusal-${stage}-${reason}`,
            kind: CampaignArtifactKind.Refusal,
            status: `refused-${stage}`,
            label: 'Campaign refused',
            narrative: renderCampaignRefusalRecord({
                plan: planForRecord,
                workspace: campaignWorkspaceSnapshot,
                reason,
                autonomyMode: campaignAutonomyMode,
                at: new Date().toISOString(),
                stage,
            }),
            aliasBindings: defaultCompoundAliasRegistry.getSnapshot(),
        });
    }, [stepParams, campaignWorkspaceSnapshot, campaignAutonomyMode]);
    recordCampaignRefusalRef.current = recordCampaignRefusal;

    let campaignSubmitLabel = 'Start guided campaign';
    if (campaignAutonomyMode === CAMPAIGN_AUTONOMY_MODES.FULL_AUTO) {
        campaignSubmitLabel = 'Start autonomous campaign';
    } else if (campaignAutonomyMode === CAMPAIGN_AUTONOMY_MODES.REVIEW_BRANCHES) {
        campaignSubmitLabel = 'Start reviewed campaign';
    }

    const handleCampaignEscalation = useCallback((decision, options = {}) => {
        const escalation = campaignPanel.escalation;
        if (!escalation || typeof escalation.respond !== 'function') return;
        // The single place a checkpoint reply is sent, so the single place to
        // refuse one. The reply below reads live atom selections and grow vectors
        // out of App.Workspace; answering out of a workspace a reconnect has not
        // put back would send the campaign evidence measured on nothing. The
        // panel hides these controls for the same reason — this is the guard
        // behind that, for a reply arriving from anywhere else.
        if (workspaceUnavailableReason) return;
        const prompt = escalation?.prompt || {};
        const currentEvidence = selectedGrowVectorEvidence(selectedAtoms, App.Workspace);
        const directedEvidence = growVectorReversed
            ? reversedSelectedGrowVectorEvidence(currentEvidence)
            : currentEvidence;
        escalation.respond(campaignEscalationReply(
            decision,
            directedEvidence,
            {
                ...prompt,
                requestedInput: prompt.requestedInput || escalation?.requestedInput || null,
                ...options,
            },
        ));
        setCampaignPanel((prev) => ({
            ...prev,
            escalation: null,
        }));
    }, [
        campaignPanel.escalation, growVectorReversed, selectedAtoms,
        workspaceUnavailableReason,
    ]);

    // ---- Step parameter change handler ----
    const handleParamChange = useCallback((stepIndex, paramName, value) => {
        planRevisionRef.current += 1;
        setStepParams((prev) => ({
            ...prev,
            [stepIndex]: { ...prev[stepIndex], [paramName]: value },
        }));
        if (state === WorkflowState.PROPOSED || state === WorkflowState.VALIDATED) {
            setState(WorkflowState.EDITING);
        }
    }, [state]);

    // ---- Validation hook ----
    const { stepValidation: computedValidation } = useWorkflowValidation(
        plan, stepParams
    );

    // Sync computed validation into local state for WorkflowSummary
    useEffect(() => {
        setStepValidation(computedValidation);
    }, [computedValidation]);

    // ---- Retry/skip handler for failed steps ----
    const handleRetry = useCallback(() => {
        if (failureResolverRef.current) {
            failureResolverRef.current('retry');
            failureResolverRef.current = null;
        }
    }, []);

    const handleSkip = useCallback(() => {
        if (failureResolverRef.current) {
            failureResolverRef.current('skip');
            failureResolverRef.current = null;
        }
    }, []);

    // ---- Submit handler ----
    const handleSubmit = useCallback(async () => {
        if (!planRef.current) return;
        if (campaignDigestRef.current) {
            setResults([{
                success: false,
                error: 'Wait for the active campaign cancellation to finish before resubmitting.',
            }]);
            return;
        }
        const submittedRevision = planRevisionRef.current;
        const submitAttempt = ++submitAttemptRef.current;
        let submittedCampaignDigest = null;

        // Merge user-edited params back into plan actions, coercing
        // number- and boolean-typed params out of their form-input
        // string representations. Without coercion, the BFD server
        // rejects payloads like `"poseCount":"2"` with a type error.
        const submittedPlan = planWithStepParamOverrides(planRef.current, stepParams);

        // Mark the WorkflowPane as actively executing. handleZap reads
        // this ref to skip wiping the plan when 'zapAll' fires from
        // inside an action handler (e.g., chooseProtein → zapAll → ...).
        // Cleared in `finally` below so even unexpected throws release
        // the guard.
        isExecutingRef.current = true;

        try {
            if (isCampaignPlan(submittedPlan)) {
                const deputy = deputyRef.current;
                if (!deputy?.enabled || typeof deputy.submitWorkflow !== 'function') {
                    throw new Error('Autonomous Campaign Deputy is not connected.');
                }
                if (!campaignWorkspaceSnapshot) {
                    throw new Error('Campaign workspace review is not ready yet.');
                }
                const reviewedWorkspaceBaseline = prepareWorkflowSubmission({
                    plan: submittedPlan,
                    workspace: campaignWorkspaceSnapshot,
                }).workspaceBaseline;
                const currentWorkspaceSnapshot = deputy.stateReader
                    ?.readForReadResult?.() || null;
                if (!currentWorkspaceSnapshot
                    || !workspaceSnapshotMatchesBaseline(
                        currentWorkspaceSnapshot,
                        reviewedWorkspaceBaseline,
                    )) {
                    if (currentWorkspaceSnapshot) {
                        setCampaignWorkspaceSnapshot(currentWorkspaceSnapshot);
                    }
                    const { message, recoverable } = campaignWorkspaceReviewMessage(
                        submittedPlan,
                        currentWorkspaceSnapshot,
                    );
                    setCampaignPanel((prev) => reduceCampaignPanelState(prev, {
                        type: 'campaign-status',
                        status: 'rejected',
                        message,
                    }));
                    setResults([{ success: false, error: message }]);
                    // A stale review that the refresh above already fixed is not a
                    // refusal — the next Start goes through. Only a plan that no
                    // longer validates leaves a document to export.
                    if (!recoverable) {
                        recordCampaignRefusal({
                            plan: submittedPlan,
                            reason: message,
                            stage: 'review',
                        });
                    }
                    setState(WorkflowState.EDITING);
                    return;
                }
            }

            setExecutionStates({});
            setProvenanceSummary(null);
            setState(WorkflowState.SUBMITTED);
            EventBroker.publish(WorkflowEvents.SUBMITTED, { plan: submittedPlan });

            if (isCampaignPlan(submittedPlan)) {
                const deputy = deputyRef.current;
                const submission = await buildWorkflowSubmission({
                    plan: submittedPlan,
                    workspace: campaignWorkspaceSnapshot,
                    autonomyMode: campaignAutonomyMode,
                });
                if (JSON.stringify(submission.policy.budget.maxStepsDerivation)
                    !== JSON.stringify(campaignBudgetDerivation)) {
                    throw new Error(
                        'Campaign budget review changed before signing. Review and submit again.',
                    );
                }
                if (submitAttemptRef.current !== submitAttempt) return;
                if (planRef.current !== plan || planRevisionRef.current !== submittedRevision) {
                    throw new Error('Workflow changed while the campaign submission was being signed.');
                }
                campaignDigestRef.current = submission.submissionDigest;
                // The previous run's narrative is no longer on screen, so it
                // rejoins the previous-narratives box rather than disappearing.
                setLiveNarrativeId(null);
                setCampaignRejectedReason(null);
                submittedCampaignDigest = submission.submissionDigest;
                setCampaignPanel(reduceCampaignPanelState(INITIAL_CAMPAIGN_PANEL_STATE, {
                    type: 'campaign-status',
                    status: 'requested',
                    message: 'Submitting the reviewed Workflow Checker campaign.',
                }));
                await deputy.submitWorkflow(submission);
            } else if (submittedPlan.intent === 'single_action') {
                // Single action — execute immediately via CmdInterpreter
                setExecutionStates({ 0: { status: ExecutionStatus.RUNNING } });
                const result = await CmdInterpreter.interpretActionPlan(submittedPlan);
                setResults([result]);
                setExecutionStates({ 0: { status: ExecutionStatus.SUCCEEDED } });
                setState(WorkflowState.COMPLETE);
                setProvenanceSummary({
                    completed: 1, failed: 0, skipped: 0, totalTime: 0,
                });
                EventBroker.publish(WorkflowEvents.COMPLETE, {
                    plan: submittedPlan, results: [result],
                });
            } else {
                // Multi-step workflow — build DAG and execute via engine
                const { workflow } = createFromActionPlan(submittedPlan);
                const validation = validate(workflow);
                if (!validation.valid) {
                    setResults([{ success: false, error: validation.errors.join('; ') }]);
                    setState(WorkflowState.EDITING);
                    return;
                }

                workflowRef.current = workflow;
                const startTime = Date.now();

                const executionResult = await executeWorkflow(workflow, App.Workspace, {
                    onStepStart: (stepIndex) => {
                        setExecutionStates((prev) => ({
                            ...prev,
                            [stepIndex]: { status: ExecutionStatus.RUNNING },
                        }));
                    },
                    onStepComplete: (si, nodeComplete, result) => {
                        setExecutionStates((prev) => {
                            const next = {
                                ...prev,
                                [si]: { status: ExecutionStatus.SUCCEEDED },
                            };
                            saveProgress(
                                submittedPlan.summary, next,
                                submittedPlan, stepParams
                            );
                            return next;
                        });
                        setResults((prev) => {
                            const next = [...prev];
                            next[si] = result;
                            return next;
                        });
                        // Export actions only *generate* the file data — the
                        // action handler returns the string but never triggers
                        // a download (the manual Export pane does that as a
                        // separate UI step). When an export step runs inside a
                        // workflow, trigger the browser download here so a file
                        // actually lands on the user's machine.
                        const exportData = result?.outputs?.data;
                        if (nodeComplete?.actionId?.startsWith('export-')
                            && typeof exportData === 'string' && exportData.length > 0) {
                            const fmt = stepParams?.[si]?.format
                                || nodeComplete.paramOverrides?.format || 'sdf';
                            downloadData(`gibbs-${nodeComplete.actionId}.${fmt}`, exportData);
                        }
                        EventBroker.publish(WorkflowEvents.STEP_COMPLETE, {
                            plan: submittedPlan, stepIndex: si, result,
                        });
                    },
                    onStepFailed: (si, _nodeFailed, error) => {
                        setExecutionStates((prev) => {
                            const next = {
                                ...prev,
                                [si]: { status: ExecutionStatus.FAILED, error },
                            };
                            saveProgress(
                                submittedPlan.summary, next,
                                submittedPlan, stepParams
                            );
                            return next;
                        });
                        setResults((prev) => {
                            const next = [...prev];
                            next[si] = { success: false, error };
                            return next;
                        });
                    },
                    onWorkflowComplete: (provenance) => {
                        const totalTime = Date.now() - startTime;
                        const entries = provenance.getSteps
                            ? provenance.getSteps() : [];
                        const completed = entries.filter(
                            (e) => e.status === ExecutionStatus.SUCCEEDED
                        ).length;
                        const failed = entries.filter(
                            (e) => e.status === ExecutionStatus.FAILED
                        ).length;
                        const skipped = entries.filter(
                            (e) => e.status === ExecutionStatus.SKIPPED
                        ).length;
                        setProvenanceSummary({
                            completed, failed, skipped, totalTime,
                        });
                        setProvenanceLog(provenance);
                        setState(WorkflowState.COMPLETE);
                        clearProgress();
                        EventBroker.publish(WorkflowEvents.COMPLETE, {
                            plan: submittedPlan, provenance,
                        });

                        // Feature 3: auto-save or prompt
                        const autoSave = getPreference(
                            'autoSaveWorkflows', false
                        );
                        const pm = App.getPermissionManager
                            ? App.getPermissionManager()
                            : null;
                        const isPaid = pm
                            && (pm.isAllowed('workflow_save')
                                || pm.isAllowed('dock'));
                        if (autoSave && isPaid) {
                            try {
                                saveToLibrary(workflow, {
                                    // A reviewer who renamed the run named the
                                    // saved copy too. Without this the library
                                    // shows the summary paragraph the header
                                    // stopped leading with.
                                    name: campaignTitleRef.current
                                        || submittedPlan.summary
                                        || 'Auto-saved Workflow',
                                });
                                setSaveMessage('Workflow saved.');
                            } catch (_) {
                                setSaveMessage(
                                    'Auto-save failed.'
                                );
                            }
                        } else {
                            setCompletedWorkflow(workflow);
                            setCompletedProvenance(
                                campaignTitleRef.current || submittedPlan.summary || ''
                            );
                            setShowSavePrompt(true);
                        }
                    },
                    onWorkflowCancelled: (stepIndex) => {
                        clearProgress();
                        // Mark remaining steps as cancelled in UI
                        setExecutionStates((prev) => {
                            const next = { ...prev };
                            const totalSteps = submittedPlan.actions?.length || 0;
                            for (let i = stepIndex; i < totalSteps; i++) {
                                if (!next[i] || next[i].status === ExecutionStatus.PENDING) {
                                    next[i] = { status: ExecutionStatus.CANCELLED };
                                }
                            }
                            return next;
                        });
                        setState(WorkflowState.COMPLETE);
                        EventBroker.publish(WorkflowEvents.CANCELLED, {
                            plan: submittedPlan, cancelledAt: stepIndex,
                        });
                    },
                    onFailureDecision: () => (
                        new Promise((resolve) => {
                            failureResolverRef.current = resolve;
                        })
                    ),
                });

                // If executor returned without callbacks handling completion
                if (!executionResult.completed
                    && executionResult.errors.length > 0
                    && state !== WorkflowState.COMPLETE) {
                    // Errors were already set via callbacks; ensure we're not stuck in SUBMITTED
                    if (state === WorkflowState.SUBMITTED) {
                        setState(WorkflowState.COMPLETE);
                    }
                }
            }
        } catch (err) {
            if (submitAttemptRef.current !== submitAttempt) return;
            campaignDigestRef.current = campaignDigestAfterSubmitFailure(
                campaignDigestRef.current,
                submittedCampaignDigest,
            );
            let message = err?.message || 'Campaign submission failed.';
            if (isCampaignPlan(submittedPlan)) {
                if (/workspace changed before submission/i.test(message)) {
                    const currentWorkspaceSnapshot = deputyRef.current?.stateReader
                        ?.readForReadResult?.() || null;
                    if (currentWorkspaceSnapshot) {
                        setCampaignWorkspaceSnapshot(currentWorkspaceSnapshot);
                    }
                    message = campaignWorkspaceReviewMessage(
                        submittedPlan,
                        currentWorkspaceSnapshot,
                    ).message;
                }
                setCampaignPanel((prev) => reduceCampaignPanelState(prev, {
                    type: 'campaign-status',
                    status: 'rejected',
                    message,
                }));
            }
            setResults([{ success: false, error: message }]);
            setState(WorkflowState.EDITING);
        } finally {
            // Release the zap-guard regardless of how execution ended.
            // After this point, normal `'zapAll'` events (user resets,
            // page navigation) will wipe the plan as expected.
            isExecutingRef.current = false;
        }
    }, [
        stepParams, state, saveProgress, clearProgress, plan,
        campaignAutonomyMode, campaignWorkspaceSnapshot, campaignBudgetDerivation,
    ]);

    // ---- Cancel workflow execution (6.3) ----
    const handleCancelWorkflow = useCallback(() => {
        submitAttemptRef.current += 1;
        if (isCampaignPlan(planRef.current)) {
            if (!campaignDigestRef.current) {
                // Hashing has not produced a server-visible digest yet. The
                // submit-attempt revision guard discards its eventual result.
                setState(WorkflowState.EDITING);
                return;
            }
            deputyRef.current?.cancelWorkflow?.(campaignDigestRef.current);
            handleCampaignEvent(null, {
                type: 'campaign-status',
                status: 'cancelling',
                message: 'Cancelling the active campaign.',
            });
            return;
        }
        cancelWorkflow();
        // Also resolve any pending failure decision
        if (failureResolverRef.current) {
            failureResolverRef.current('abort');
            failureResolverRef.current = null;
        }
    }, [handleCampaignEvent]);

    // ---- Cancel/discard handler (reset everything) ----
    const handleCancel = useCallback(() => {
        submitAttemptRef.current += 1;
        cancelWorkflow(); // Cancel if running
        if (campaignDigestRef.current) {
            deputyRef.current?.cancelWorkflow?.(campaignDigestRef.current);
        }
        if (failureResolverRef.current) {
            failureResolverRef.current('abort');
            failureResolverRef.current = null;
        }
        setPlan(null);
        setCampaignTitle('');
        setStepParams({});
        setStepValidation({});
        setResults([]);
        setExecutionStates({});
        setProvenanceSummary(null);
        setProvenanceLog(null);
        setCampaignAutonomyMode(DEFAULT_CAMPAIGN_AUTONOMY_MODE);
        setCampaignTitle('');
        setState(WorkflowState.EMPTY);
    }, []);

    return (
        <SidePanelContainer
            title={WORKFLOW_PANEL_TITLE}
            open={open}
            handleClose={handleClose}
            isStacked={isStacked}
            paneId="workflow"
            longLived
            containerStyle={{
                // 2026-06-01: match HelpPanel's light-green container so
                // the 8px content margins inside `.sidepanel-container`
                // don't show as a stark black band in stacked mode. The
                // colorscheme default is 'black' (themes.js:42), which
                // was painting the inner-container background dark while
                // the inner content stayed white — visible as a black
                // strip to the right of the per-pane handle when Help
                // (which DOES override containerStyle) sits to its left.
                background: '#DCF0E6',
                zIndex: 0,
            }}
        >
            <ErrorBoundary surfaceName="Workflow Checker">

                {/* Stale-action guard: block load when saved workflow
                references an action that no longer exists */}
                {loadError && (
                <Box
                    sx={{
                        px: 1.5,
                        py: 1,
                        backgroundColor: '#ffebee',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                    data-testid="load-error-banner"
                >
                    <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ mb: 0.5 }}
                    >
                        Can&apos;t load &ldquo;
                        {loadError.name}
                        &rdquo;
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        This workflow references
                        {loadError.unknownActions.length === 1
                            ? ' an action that no longer exists: '
                            : ' actions that no longer exist: '}
                        <strong>{loadError.unknownActions.join(', ')}</strong>
                        . Delete this workflow and record a new one.
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setLoadError(null)}
                        data-testid="dismiss-load-error-btn"
                    >
                        Dismiss
                    </Button>
                </Box>
                )}

            {/*
                2026-06-04 (W4): preview-time precondition warnings.
                Surfaces unsatisfied preconditions from validate() so the
                user sees them BEFORE submit. Each warning groups by
                action name so a single missing guarantee that's
                required by multiple downstream steps reads as one
                heading. The workflow is still submittable — the
                warnings are advisory because workspace state can
                satisfy a precondition outside the workflow DAG (e.g.,
                user already has a fragment library loaded). But the
                user no longer faces a silent runtime failure with no
                hint as to why.

                Demo day 2026-06-03: "do a hit to lead on hep b" → 10
                steps; step 5 (grow-from-atom) needs fragment_map_avail
                with nothing upstream providing it. Pre-fix the user
                submitted, step 1 succeeded, then the workflow died
                silently. Post-fix the user sees the warning and either
                makes sure the workspace has fragments OR runs
                fragment-sim first OR proceeds and accepts the risk.
            */}
            {planWarnings && planWarnings.length > 0 && (
                <Box
                    sx={{
                        px: 1.5,
                        py: 1,
                        backgroundColor: '#fff4e5',
                        borderLeft: '4px solid #ed6c02',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                    data-testid="plan-precondition-warnings"
                >
                    {/* Collapsible: top line always shows; detail is hidden
                        until expanded. Starts collapsed (warningsExpanded). */}
                    <Box
                        onClick={() => setWarningsExpanded((v) => !v)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <IconButton
                            size="small"
                            aria-label="toggle warnings"
                            sx={{ mr: 0.5, color: '#663c00' }}
                        >
                            {warningsExpanded
                                ? <ExpandLessIcon fontSize="small" />
                                : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                        <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{ color: '#663c00' }}
                        >
                            ⚠ Step preconditions may not be satisfied
                        </Typography>
                    </Box>
                    <Collapse in={warningsExpanded}>
                        <Typography
                            variant="body2"
                            sx={{ mb: 0.5, mt: 0.5, color: '#663c00' }}
                        >
                            {`${planWarnings.length} `}
                            {planWarnings.length === 1 ? 'guarantee is' : 'guarantees are'}
                            {' not produced by any upstream step in this plan. '}
                            These may already be satisfied by your current
                            workspace (e.g., a previously loaded protein or
                            fragment library). If not, the step will fail
                            when it runs.
                        </Typography>
                        <Box
                            component="ul"
                            sx={{
                                pl: 2.5,
                                mb: 0,
                                color: '#663c00',
                                fontSize: '0.85rem',
                            }}
                        >
                            {planWarnings.map((w) => (
                                <li key={`${w.nodeId}:${w.check}`} data-testid={`plan-warning-${w.check}`}>
                                    <strong>{w.actionName}</strong>
                                    {' requires '}
                                    <code>{w.check}</code>
                                    {w.description ? ` — ${w.description}` : ''}
                                </li>
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            )}

            {/* Feature 5: Resume prompt for saved progress */}
            {showResumePrompt && savedProgress && (
                <Box
                    sx={{
                        px: 1.5,
                        py: 1,
                        backgroundColor: '#e3f2fd',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                    data-testid="resume-workflow-prompt"
                >
                    <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ mb: 0.5 }}
                    >
                        Resume previous workflow?
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        {savedProgress.planSummary}
                        {' — '}
                        {new Date(savedProgress.lastUpdate)
                            .toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            variant="contained"
                            data-testid="resume-workflow-btn"
                            disabled={!savedProgress?.plan}
                            onClick={handleResumeWorkflow}
                        >
                            Resume
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            data-testid="dismiss-resume-btn"
                            onClick={() => {
                                setShowResumePrompt(false);
                                clearProgress();
                            }}
                        >
                            Dismiss
                        </Button>
                    </Box>
                </Box>
                )}

                {state === WorkflowState.EMPTY ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No workflow loaded. Ask Gibbs to plan a
                            workflow and it will appear here for review.
                        </Typography>
                        {/*
                          * The documents outlive the workflow. With no plan loaded
                          * the campaign panel does not render at all, so a
                          * narrative from a run this session finished — or one a
                          * session file brought in — was held but unreachable
                          * until somebody proposed a new workflow.
                          */}
                        <SessionNarrativeList
                            artifacts={campaignArtifacts}
                            markdownFormatterFor={artifactMarkdownFormatter}
                            onDiscard={() => defaultCampaignArtifactRegistry.clear()}
                        />
                    </Box>
                ) : (
                    <>
                        <WorkflowSummary
                            plan={plan}
                            state={state}
                            stepValidation={stepValidation}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            onCancelWorkflow={handleCancelWorkflow}
                            provenanceSummary={provenanceSummary}
                            provenanceLog={provenanceLog}
                            workflow={workflowRef.current}
                            workspace={App.Workspace}
                            submitLabel={campaignPlan ? campaignSubmitLabel : 'Submit'}
                            submitBlockedReason={campaignPlan ? campaignSubmitBlockedReason : null}
                            refusalRecord={campaignRefusalRecord}
                            formatMarkdown={formatCampaignMarkdown}
                            title={campaignTitle}
                            onTitleChange={setCampaignTitle}
                        />
                        <WorkflowStepList
                            actions={plan?.actions || []}
                            stepParams={stepParams}
                            stepValidation={stepValidation}
                            onParamChange={handleParamChange}
                            results={results}
                            executionStates={executionStates}
                            onRetry={campaignPlan ? null : handleRetry}
                            onSkip={campaignPlan ? null : handleSkip}
                            disabled={state === WorkflowState.SUBMITTED}
                            stepsExpanded={stepsExpanded}
                            onToggleSteps={setStepsExpanded}
                            header={(
                                <WorkflowCampaignPanel
                                    campaignPanel={campaignPanel}
                                    onEscalationDecision={handleCampaignEscalation}
                                    campaignAutonomyMode={campaignAutonomyMode}
                                    onCampaignAutonomyModeChange={setCampaignAutonomyMode}
                                    campaignArtifacts={campaignArtifacts.filter(
                                        (entry) => entry.id !== liveNarrativeId,
                                    )}
                                    onDiscardNarratives={
                                        () => defaultCampaignArtifactRegistry.clear()
                                    }
                                    artifactMarkdownFormatter={artifactMarkdownFormatter}
                                    checkpointBlockedReason={workspaceUnavailableReason}
                                    unresolvedEarlierCampaign={unresolvedEarlierCampaign}
                                    onDiscardEarlierCampaign={
                                        () => setUnresolvedEarlierCampaign(null)
                                    }
                                    dockingSite={dockingSite}
                                    dockingSiteLocked={dockingSiteLocked}
                                    onDockingSiteChange={handleDockingSiteChange}
                                    configurationEnabled={campaignPlan}
                                    campaignBudgetDerivation={campaignBudgetDerivation}
                                    referenceRoleSummary={referenceRoleSummary}
                                    growCheckpointRequired={campaignGrowCheckpointRequired}
                                    selectedAtomCount={selectedAtomUniqueIds().length}
                                    selectedGrowVector={selectedGrowVector}
                                    growVectorReversed={growVectorReversed}
                                    onGrowVectorReverseChange={handleGrowVectorReverseChange}
                                    getDataConnection={() => App.PrimaryDataConnection}
                                    formatCompoundId={formatCampaignCompoundId}
                                    formatText={formatCampaignText}
                                    formatMarkdown={formatCampaignMarkdown}
                                />
                        )}
                        />
                    </>
                )}

                {/* Feature 3: Save workflow prompt after completion */}
                {saveMessage && (
                <Box sx={{ px: 1.5, py: 1, backgroundColor: '#e8f5e9' }}>
                    <Typography variant="body2">
                        {saveMessage}
                    </Typography>
                </Box>
                )}
                {showSavePrompt && (
                <Box
                    sx={{
                        px: 1.5,
                        py: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: '#fff8e1',
                    }}
                    data-testid="save-workflow-prompt"
                >
                    <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ mb: 0.5 }}
                    >
                        Save this workflow?
                    </Typography>
                    <Box sx={{
                        display: 'flex', gap: 1, flexWrap: 'wrap',
                    }}
                    >
                        <Button
                            size="small"
                            variant="contained"
                            data-testid="save-workflow-btn"
                            onClick={() => {
                                if (completedWorkflow) {
                                    try {
                                        saveToLibrary(
                                            completedWorkflow,
                                            {
                                                name: completedProvenance
                                                    || 'Saved Workflow',
                                            }
                                        );
                                        setSaveMessage(
                                            'Workflow saved.'
                                        );
                                    } catch (_) {
                                        setSaveMessage(
                                            'Save failed.'
                                        );
                                    }
                                }
                                setShowSavePrompt(false);
                                if (savedWorkflowsOpen) {
                                    setSavedWorkflows(
                                        listWorkflows()
                                    );
                                }
                            }}
                        >
                            Save
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            data-testid="dont-save-workflow-btn"
                            onClick={() => {
                                setShowSavePrompt(false);
                            }}
                        >
                            Don&apos;t Save
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            data-testid="always-save-workflow-btn"
                            onClick={() => {
                                const pm = App.getPermissionManager
                                    ? App.getPermissionManager()
                                    : null;
                                const isPaid = pm
                                    && (pm.isAllowed('workflow_save')
                                        || pm.isAllowed('dock'));
                                if (isPaid) {
                                    savePreference(
                                        'autoSaveWorkflows', true
                                    );
                                    if (completedWorkflow) {
                                        try {
                                            saveToLibrary(
                                                completedWorkflow,
                                                {
                                                    name:
                                                        completedProvenance
                                                        || 'Saved Workflow',
                                                }
                                            );
                                            setSaveMessage(
                                                'Workflow saved.'
                                                + ' Auto-save enabled.'
                                            );
                                        } catch (_) {
                                            setSaveMessage(
                                                'Save failed.'
                                            );
                                        }
                                    }
                                } else {
                                    setSaveMessage(
                                        'Upgrade to Pro to enable'
                                        + ' auto-save.'
                                    );
                                }
                                setShowSavePrompt(false);
                                if (savedWorkflowsOpen) {
                                    setSavedWorkflows(
                                        listWorkflows()
                                    );
                                }
                            }}
                        >
                            Always Save (Pro)
                        </Button>
                    </Box>
                </Box>
                )}

                {/* Feature 4: Macro naming dialog */}
                {showMacroNamer && (
                <Box
                    sx={{
                        px: 1.5,
                        py: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: '#f3e5f5',
                    }}
                    data-testid="macro-namer-dialog"
                >
                    <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ mb: 0.5 }}
                    >
                        Name this macro
                    </Typography>
                    <MuiTextField
                        size="small"
                        fullWidth
                        label="Name"
                        value={macroName}
                        onChange={(e) => setMacroName(
                            e.target.value
                        )}
                        sx={{ mb: 1 }}
                        data-testid="macro-name-input"
                    />
                    <MuiTextField
                        size="small"
                        fullWidth
                        label="Description (optional)"
                        value={macroDescription}
                        onChange={(e) => setMacroDescription(
                            e.target.value
                        )}
                        sx={{ mb: 1 }}
                        data-testid="macro-desc-input"
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleSaveMacro}
                            data-testid="save-macro-btn"
                        >
                            Save Macro
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleCancelMacroNamer}
                            data-testid="cancel-macro-btn"
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
                )}

                {/* Re-run dialog: inline parameter editor for loaded workflow with variables */}
                {rerunWorkflow && (
                <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }} data-testid="rerun-dialog">
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Re-run:
                        {' '}
                        {rerunWorkflow.metadata?.name || 'Workflow'}
                    </Typography>
                    {rerunVariables.map((v) => (
                        <Box key={`${v.nodeId}-${v.paramName}`} sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                {v.variableName}
                            </Typography>
                            <input
                                type="text"
                                value={rerunValues[v.variableName] ?? ''}
                                onChange={(e) => handleRerunVariableChange(
                                    v.variableName, e.target.value
                                )}
                                style={{ width: '100%', padding: '4px 8px', fontSize: '0.875rem' }}
                                data-testid={`rerun-var-${v.variableName}`}
                            />
                        </Box>
                    ))}
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleRerunExecute}
                        data-testid="rerun-execute-btn"
                    >
                        Run
                    </Button>
                </Box>
                )}

            {/* Saved Workflows collapsible section */}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        cursor: 'pointer',
                    }}
                    onClick={() => setSavedWorkflowsOpen(!savedWorkflowsOpen)}
                    data-testid="saved-workflows-toggle"
                >
                    {savedWorkflowsOpen
                        ? <ExpandLessIcon fontSize="small" sx={{ mr: 0.5 }} />
                        : <ExpandMoreIcon fontSize="small" sx={{ mr: 0.5 }} />}
                    <Typography variant="subtitle2" fontWeight="bold">
                        Saved Workflows
                    </Typography>
                </Box>
                <Collapse in={savedWorkflowsOpen}>
                    {savedWorkflows.length === 0 ? (
                        <Box sx={{ px: 1.5, pb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                No saved workflows yet.
                            </Typography>
                        </Box>
                    ) : (
                        <List dense disablePadding data-testid="saved-workflows-list">
                            {savedWorkflows.map((wf) => (
                                <ListItem
                                    key={wf.id}
                                    alignItems="flex-start"
                                    sx={{
                                        px: 1.5,
                                        // 2026-06-09: stack Load above Delete vertically so the
                                        // right-side control column is narrower (~56px) than
                                        // the prior horizontal layout (~88px). That gives the
                                        // workflow name ~32px more horizontal room, which
                                        // matters because `saveToLibrary` stores
                                        // `submittedPlan.summary` (a multi-sentence
                                        // description) as the `name` field — long prose was
                                        // overlapping the buttons when single-line truncation
                                        // (`noWrap: true`) failed to engage in the wrapped
                                        // layout. Replaces UI-debt log 2026-04-23 fix (the
                                        // 88px reservation no longer fits long prose).
                                        pr: '56px',
                                    }}
                                >
                                    <ListItemText
                                        primary={wf.name}
                                        secondary={new Date(wf.date).toLocaleDateString()}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            // Native title attribute reveals the full name on
                                            // hover for truncated text.
                                            title: wf.name,
                                            sx: {
                                                // Multi-line wrap, clamp to 3 lines, ellipsize
                                                // the rest. Works around the
                                                // `summary`-as-`name` data pattern without
                                                // requiring a save-side rename.
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                wordBreak: 'break-word',
                                            },
                                        }}
                                        secondaryTypographyProps={{
                                            variant: 'caption',
                                            noWrap: true,
                                        }}
                                    />
                                    <ListItemSecondaryAction
                                        sx={{
                                            // Vertical stack of Load + Delete. Top-aligned with
                                            // the first line of the name text so the controls
                                            // ride consistently regardless of how many lines
                                            // the name wraps to.
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 0.25,
                                            top: 8,
                                            transform: 'none',
                                        }}
                                    >
                                        <Button
                                            size="small"
                                            sx={{ minWidth: 0, px: 1, py: 0.25 }}
                                            onClick={() => handleLoadWorkflow(wf.id)}
                                            data-testid={`load-workflow-${wf.id}`}
                                        >
                                            Load
                                        </Button>
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            onClick={() => handleDeleteWorkflow(wf.id)}
                                            data-testid={`delete-workflow-${wf.id}`}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Collapse>
                </Box>

            {/* Record Macro toggle — pinned below Saved Workflows at the bottom. */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderTop: '1px solid',
                borderColor: 'divider',
            }}
            >
                <Button
                    size="small"
                    variant={recording ? 'contained' : 'outlined'}
                    color={recording ? 'error' : 'primary'}
                    onClick={handleToggleRecording}
                    data-testid="record-macro-btn"
                >
                    {recording ? 'Stop Recording' : 'Record Macro'}
                </Button>
                {recording && (
                <FiberManualRecordIcon
                    sx={{ color: 'error.main', fontSize: 14, animation: 'pulse 1s infinite' }}
                    data-testid="recording-indicator"
                />
                )}
            </Box>
            </ErrorBoundary>
        </SidePanelContainer>
    );
}
